import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { FilePreviewData } from '../services/types';

// Re-export the shared interface
export type { FilePreviewData };

export interface FileProcessorState {
  isProcessing: boolean;
  error: string | null;
  previewData: FilePreviewData | null;
  fileType: 'csv' | 'excel' | null;
}

export const useFileProcessor = () => {
  const [state, setState] = useState<FileProcessorState>({
    isProcessing: false,
    error: null,
    previewData: null,
    fileType: null,
  });

  const processFile = useCallback(async (file: File) => {
    setState(prev => ({ ...prev, isProcessing: true, error: null }));

    try {
      const fileExtension = file.name.toLowerCase().split('.').pop();
      let fileType: 'csv' | 'excel';
      let previewData: FilePreviewData;

      if (fileExtension === 'csv') {
        fileType = 'csv';
        previewData = await processCSVFile(file);
      } else if (['xlsx', 'xls'].includes(fileExtension || '')) {
        fileType = 'excel';
        previewData = await processExcelFile(file);
      } else {
        throw new Error('Unsupported file type. Please upload a CSV or Excel file.');
      }

      setState({
        isProcessing: false,
        error: null,
        previewData,
        fileType,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to process file',
      }));
    }
  }, []);

  const selectExcelSheet = useCallback(async (sheetName: string, file: File) => {
    if (!state.previewData?.sheetNames || state.fileType !== 'excel') return;

    setState(prev => ({ ...prev, isProcessing: true, error: null }));

    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

          if (jsonData.length === 0) {
            throw new Error('Selected Excel sheet is empty');
          }

          // Use current header row settings or default to first row
          const headerRowIndex = state.previewData?.headerRowIndex ?? 0;
          const firstRowIsHeader = state.previewData?.firstRowIsHeader ?? true;
          
          const rawHeaders = jsonData[headerRowIndex] || [];
          const rawRows = jsonData.slice(headerRowIndex + 1);

          // Split columns that contain delimiters (Excel files)
          const { headers, rows } = splitDelimiterColumns(rawHeaders, rawRows, ',');

          let columnMapping;
          try {
            columnMapping = detectColumns(headers, rows);
          } catch (error) {
            console.warn('Column detection failed:', error);
            columnMapping = {};
          }

          setState(prev => ({
            ...prev,
            isProcessing: false,
            error: null,
            previewData: {
              ...prev.previewData!,
              headers,
              rows,
              selectedSheet: sheetName,
              columnMapping,
              headerRowIndex,
              firstRowIsHeader,
            },
          }));
        } catch (error) {
          setState(prev => ({
            ...prev,
            isProcessing: false,
            error: error instanceof Error ? error.message : 'Failed to process selected sheet',
          }));
        }
      };

      reader.onerror = () => {
        setState(prev => ({
          ...prev,
          isProcessing: false,
          error: 'Failed to read file',
        }));
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to process selected sheet',
      }));
    }
  }, [state.previewData?.sheetNames, state.fileType, state.previewData?.headerRowIndex, state.previewData?.firstRowIsHeader]);

  const updateColumnMapping = useCallback((field: 'ean' | 'quantity', columnName: string) => {
    setState(prev => ({
      ...prev,
      previewData: prev.previewData ? {
        ...prev.previewData,
        columnMapping: {
          ...prev.previewData.columnMapping,
          [field]: columnName,
        },
      } : null,
    }));
  }, []);

  const updateHeaderRowSettings = useCallback((firstRowIsHeader: boolean, headerRowIndex?: number) => {
    setState(prev => ({
      ...prev,
      previewData: prev.previewData ? {
        ...prev.previewData,
        firstRowIsHeader,
        headerRowIndex: headerRowIndex ?? 0,
      } : null,
    }));
  }, []);



  const reprocessWithNewHeaderRow = useCallback(async (file: File, firstRowIsHeader: boolean, headerRowIndex: number) => {
    if (!file) return;

    setState(prev => ({ ...prev, isProcessing: true, error: null }));

    try {
      const fileExtension = file.name.toLowerCase().split('.').pop();
      let previewData: FilePreviewData;

      if (fileExtension === 'csv') {
        previewData = await processCSVFileWithHeaderRow(file, firstRowIsHeader, headerRowIndex);
      } else if (['xlsx', 'xls'].includes(fileExtension || '')) {
        previewData = await processExcelFileWithHeaderRow(file, firstRowIsHeader, headerRowIndex, state.previewData?.selectedSheet);
      } else {
        throw new Error('Unsupported file type. Please upload a CSV or Excel file.');
      }

      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: null,
        previewData,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to reprocess file',
      }));
    }
  }, [state.previewData?.selectedSheet]);

  const reset = useCallback(() => {
    setState({
      isProcessing: false,
      error: null,
      previewData: null,
      fileType: null,
    });
  }, []);

  return {
    ...state,
    processFile,
    selectExcelSheet,
    updateColumnMapping,
    updateHeaderRowSettings,
    reprocessWithNewHeaderRow,
    reset,
  };
};

const detectColumns = (headers: string[], rows: string[][]): { ean?: string; quantity?: string } => {
  const columnMapping: { ean?: string; quantity?: string } = {};

  try {
    const eanCandidates: { header: string; confidence: number }[] = [];
    const quantityCandidates: { header: string; confidence: number }[] = [];

    // Analyze each column
    headers.forEach((header, columnIndex) => {
      const columnValues = rows.map(row => row[columnIndex]).filter(value => value && value.trim() !== '');
      
      if (columnValues.length === 0) return;

      // Check for EAN column (13-digit numbers)
      const eanMatches = columnValues.filter(value => {
        try {
          const cleanValue = value.toString().replace(/\D/g, ''); // Remove non-digits
          return cleanValue.length === 13 && /^\d{13}$/.test(cleanValue);
        } catch {
          return false;
        }
      });

      // Check for quantity column (positive integers)
      const quantityMatches = columnValues.filter(value => {
        try {
          const numValue = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
          return Number.isInteger(numValue) && numValue > 0;
        } catch {
          return false;
        }
      });

      // Calculate confidence scores
      const eanConfidence = eanMatches.length / columnValues.length;
      const quantityConfidence = quantityMatches.length / columnValues.length;

      // Collect candidates that meet the confidence thresholds
      if (eanConfidence >= 0.7) {
        eanCandidates.push({ header, confidence: eanConfidence });
      }
      
      if (quantityConfidence >= 0.8) {
        quantityCandidates.push({ header, confidence: quantityConfidence });
      }
    });

    // Only assign if there's exactly one candidate for each type
    if (eanCandidates.length === 1) {
      columnMapping.ean = eanCandidates[0].header;
    }
    
    if (quantityCandidates.length === 1) {
      columnMapping.quantity = quantityCandidates[0].header;
    }
  } catch (error) {
    console.warn('Column detection failed, returning empty mapping:', error);
    return {};
  }

  return columnMapping;
};

/**
 * Splits columns that contain delimiters into multiple columns
 * @param headers - Array of header names
 * @param rows - Array of data rows
 * @param delimiter - The delimiter used in the original file
 * @returns Object with new headers and rows after splitting delimiter-containing columns
 */
const splitDelimiterColumns = (headers: string[], rows: string[][], delimiter: string = ','): { headers: string[]; rows: string[][] } => {
  try {
    // Handle edge cases
    if (!headers || headers.length === 0) {
      return { headers: [], rows: [] };
    }
    
    if (!rows || rows.length === 0) {
      return { headers: [...headers], rows: [] };
    }
  

  
  const newHeaders: string[] = [];
  const columnSplitInfo: { [columnIndex: number]: { hasDelimiter: boolean; maxSplitCount: number } } = {};
  
      // First pass: analyze each column to determine if it needs splitting
    headers.forEach((header, columnIndex) => {
      // Check if this column contains the delimiter in any row
      const hasDelimiter = rows.some(row => {
        const cellValue = row[columnIndex] || '';
        
        if(typeof cellValue !== 'string') {
          return false;
        }
        return cellValue.includes(delimiter);
      });
      
      if (hasDelimiter) {
        // Find the maximum number of delimiter-separated values across all rows
        let maxSplitCount = 1;
        rows.forEach(row => {
          const cellValue = row[columnIndex] || '';
          const splitCount = cellValue.split(delimiter).length;
          maxSplitCount = Math.max(maxSplitCount, splitCount);
        });
      
              columnSplitInfo[columnIndex] = { hasDelimiter: true, maxSplitCount };
        
        // Create new column headers for this split column
        for (let i = 0; i < maxSplitCount; i++) {
          newHeaders.push(i === 0 ? header : `${header}[${i}]`);
        }
      } else {
        columnSplitInfo[columnIndex] = { hasDelimiter: false, maxSplitCount: 1 };
      // Keep the original column as is
      newHeaders.push(header);
    }
  });
  
  // Second pass: process each row
  const newRows: string[][] = [];
  rows.forEach(row => {
    const newRow: string[] = [];
    
    headers.forEach((header, columnIndex) => {
      const cellValue = row[columnIndex] || '';
      const splitInfo = columnSplitInfo[columnIndex];
      
      if (splitInfo.hasDelimiter) {
        // Split the cell value by the delimiter
        const splitValues = cellValue.split(delimiter).map(val => val.trim());
        
        // Add all split values, padding with empty strings if needed
        for (let i = 0; i < splitInfo.maxSplitCount; i++) {
          newRow.push(splitValues[i] || '');
        }
      } else {
        // Keep the original cell value
        newRow.push(cellValue);
      }
    });
    
    newRows.push(newRow);
  });
  
  return { headers: newHeaders, rows: newRows };
  } catch (error) {
    console.error('Error in splitDelimiterColumns:', error);
    // Return original data if splitting fails
    return { headers, rows };
  }
};



const processCSVFile = async (file: File): Promise<FilePreviewData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }

          const data = results.data as string[][];
          
          if (data.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }

          // Default to first row as header
          const headerRowIndex = 0;
          const firstRowIsHeader = true;
          
          const rawHeaders = data[headerRowIndex] || [];
          const rawRows = data.slice(headerRowIndex + 1);

          // Get the detected delimiter from Papa Parse results
          const delimiter = results.meta.delimiter || ',';

          // Split columns that contain the detected delimiter
          const { headers, rows } = splitDelimiterColumns(rawHeaders, rawRows, delimiter);

          let columnMapping;
          try {
            columnMapping = detectColumns(headers, rows);
          } catch (error) {
            console.warn('Column detection failed:', error);
            columnMapping = {};
          }

          resolve({
            headers,
            rows,
            columnMapping,
            headerRowIndex,
            firstRowIsHeader,
            delimiter,
          });
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      }
    });
  });
};

const processExcelFile = async (file: File): Promise<FilePreviewData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheetNames = workbook.SheetNames;
        if (sheetNames.length === 0) {
          reject(new Error('Excel file contains no sheets'));
          return;
        }

        // Get the first sheet by default
        const firstSheetName = sheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

        if (jsonData.length === 0) {
          reject(new Error('Excel sheet is empty'));
          return;
        }

        // Default to first row as header
        const headerRowIndex = 0;
        const firstRowIsHeader = true;
        
        const rawHeaders = jsonData[headerRowIndex] || [];
        const rawRows = jsonData.slice(headerRowIndex + 1);

        // Split columns that contain delimiters (Excel files)
        const { headers, rows } = splitDelimiterColumns(rawHeaders, rawRows, ',');

        let columnMapping;
        try {
          columnMapping = detectColumns(headers, rows);
        } catch (error) {
          console.warn('Column detection failed:', error);
          columnMapping = {};
        }

        resolve({
          headers,
          rows,
          sheetNames,
          selectedSheet: firstSheetName,
          columnMapping,
          headerRowIndex,
          firstRowIsHeader,
        });
      } catch (error) {
        console.error('Error in processExcelFile:', error);
        reject(new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

const processCSVFileWithHeaderRow = async (file: File, firstRowIsHeader: boolean, headerRowIndex: number): Promise<FilePreviewData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }

          const data = results.data as string[][];
          
          if (data.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }

          if (headerRowIndex >= data.length) {
            reject(new Error('Header row index is out of bounds'));
            return;
          }

          const rawHeaders = data[headerRowIndex] || [];
          const rawRows = data.slice(headerRowIndex + 1);

          // Get the detected delimiter from Papa Parse results
          const delimiter = results.meta.delimiter || ',';

          // Split columns that contain the detected delimiter
          const { headers, rows } = splitDelimiterColumns(rawHeaders, rawRows, delimiter);

          let columnMapping;
          try {
            columnMapping = detectColumns(headers, rows);
          } catch (error) {
            console.warn('Column detection failed:', error);
            columnMapping = {};
          }

          resolve({
            headers,
            rows,
            columnMapping,
            headerRowIndex,
            firstRowIsHeader,
            delimiter,
          });
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      }
    });
  });
};

const processExcelFileWithHeaderRow = async (file: File, firstRowIsHeader: boolean, headerRowIndex: number, selectedSheet?: string): Promise<FilePreviewData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheetNames = workbook.SheetNames;
        if (sheetNames.length === 0) {
          reject(new Error('Excel file contains no sheets'));
          return;
        }

        // Use selected sheet or first sheet
        const sheetName = selectedSheet || sheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

        if (jsonData.length === 0) {
          reject(new Error('Excel sheet is empty'));
          return;
        }

        if (headerRowIndex >= jsonData.length) {
          reject(new Error('Header row index is out of bounds'));
          return;
        }
        
        const rawHeaders = jsonData[headerRowIndex] || [];
        const rawRows = jsonData.slice(headerRowIndex + 1);

                  // Split columns that contain delimiters (Excel files)
          const { headers, rows } = splitDelimiterColumns(rawHeaders, rawRows, ',');

        let columnMapping;
        try {
          columnMapping = detectColumns(headers, rows);
        } catch (error) {
          console.warn('Column detection failed:', error);
          columnMapping = {};
        }

        resolve({
          headers,
          rows,
          sheetNames,
          selectedSheet: sheetName,
          columnMapping,
          headerRowIndex,
          firstRowIsHeader,
        });
      } catch (error) {
        reject(new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}; 